// usePackingScanXL - Sprint 44 Fase 3 Vista 7.
// Hook de pantalla de packing scan. Beep WebAudio + flash + actualizar
// caja_codigo/peso (PUT /:id) + cerrar caja (POST /:id/completar dispara
// kardex SALIDA backend) + preparar envio. Reusa packingService legacy.
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as svc from '../../packing/services/packingService';

function beep(freq, durationMs) {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = 'square';
    o.frequency.value = freq || 440;
    g.gain.value = 0.1;
    o.connect(g); g.connect(ctx.destination);
    o.start();
    setTimeout(() => {
      try { o.stop(); ctx.close(); } catch (_) { /* ignore */ }
    }, durationMs || 120);
  } catch (_) { /* webaudio no disponible */ }
}

export default function usePackingScanXL() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [packing, setPacking]   = useState(null);
  const [loaded, setLoaded]     = useState(false);
  const [scanning, setScanning] = useState(false);
  const [flash, setFlash]       = useState(null);
  const [completing, setCompleting] = useState(false);

  const refresh = useCallback(async () => {
    try { setPacking(await svc.getPacking(id)); }
    catch (_) {
      setFlash({ kind: 'err', text: 'No se pudo cargar' });
      setTimeout(() => setFlash(null), 2000);
    }
  }, [id]);

  useEffect(() => {
    let alive = true;
    (async () => {
      await refresh();
      if (alive) setLoaded(true);
    })();
    return () => { alive = false; };
  }, [refresh]);

  const onScan = useCallback(async (code) => {
    try {
      const r = await svc.scanPacking(id, code);
      beep(880, 80);
      const nombre = (r && r.linea && r.linea.producto_nombre) || code;
      setFlash({ kind: 'ok', text: '\u2713 ' + nombre });
      if (r && r.packing) setPacking(r.packing);
      setTimeout(() => setFlash(null), 1200);
    } catch (e) {
      beep(220, 300);
      setFlash({ kind: 'err', text: (e && e.message) || 'Error' });
      setTimeout(() => setFlash(null), 2000);
    }
  }, [id]);

  const onGuardarCaja = useCallback(async (payload) => {
    try {
      const r = await svc.updatePacking(id, payload);
      if (r) setPacking((pk) => ({ ...pk, ...r }));
      setFlash({ kind: 'ok', text: 'Datos guardados' });
      setTimeout(() => setFlash(null), 1200);
    } catch (e) {
      setFlash({ kind: 'err', text: (e && e.message) || 'Error' });
      setTimeout(() => setFlash(null), 2000);
    }
  }, [id]);

  const onCerrar = useCallback(async () => {
    if (!window.confirm('Cerrar caja? Esto descuenta stock (kardex SALIDA).')) return;
    setCompleting(true);
    try {
      await svc.completarPacking(id);
      setFlash({ kind: 'ok', text: 'Caja cerrada \u00b7 stock descontado' });
      await refresh();
      setTimeout(() => setFlash(null), 1500);
    } catch (e) {
      setFlash({ kind: 'err', text: (e && e.message) || 'Error al cerrar caja' });
      setTimeout(() => setFlash(null), 2000);
    } finally {
      setCompleting(false);
    }
  }, [id, refresh]);

  const onImprimirEtiqueta = useCallback(async () => {
    try {
      const data = await svc.prepararEnvio(id);
      // Visor minimalista: mostrar JSON en una nueva ventana legible.
      const text = JSON.stringify(data, null, 2);
      const w = window.open('', '_blank');
      if (w) {
        w.document.title = 'Datos de envio packing #' + id;
        w.document.body.style.fontFamily = 'monospace';
        w.document.body.style.padding = '20px';
        w.document.body.style.whiteSpace = 'pre-wrap';
        w.document.body.textContent = text;
      } else {
        alert('Datos de envio:\n' + text);
      }
    } catch (e) {
      setFlash({ kind: 'err', text: (e && e.message) || 'Error' });
      setTimeout(() => setFlash(null), 2000);
    }
  }, [id]);

  // ESC cierra modo escaneo
  useEffect(() => {
    if (!scanning) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setScanning(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [scanning]);

  return {
    id, navigate, packing, loaded,
    scanning, setScanning, flash, completing,
    onScan, onGuardarCaja, onCerrar, onImprimirEtiqueta, refresh,
  };
}
